import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const WISHLIST_COUNT_KEY = 'homi-header-wishlist-count';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {MutationObserver | null} */
  #wishlistObserver = null;

  /** @type {MutationObserver | null} */
  #wishlistCountObserver = null;

  /** @type {Element | null} */
  #countObservedWrap = null;

  /** @type {MutationObserver | null} */
  #signedInAccountObserver = null;

  /** @type {ResizeObserver | null} */
  #accountDialogResize = null;

  /** @type {(() => void) | null} */
  #placeAccountDialog = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#restoreWishlistCount();
    this.#placeWishlistIcon();
    this.#bindSignedInAccount();

    const column = this.closest('.header__column--right') || this.parentElement;
    const columns = column?.closest('.header__columns');
    if (column) {
      this.#wishlistObserver = new MutationObserver(() => this.#placeWishlistIcon());
      this.#wishlistObserver.observe(column, { childList: true });
      this.#wishlistObserver.observe(this, { childList: true });
      if (columns && columns !== column) {
        this.#wishlistObserver.observe(columns, { childList: true });
      }
    }
  }

  disconnectedCallback() {
    this.#wishlistObserver?.disconnect();
    this.#wishlistCountObserver?.disconnect();
    this.#signedInAccountObserver?.disconnect();
    this.#accountDialogResize?.disconnect();
    if (this.#placeAccountDialog) window.removeEventListener('resize', this.#placeAccountDialog);
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * The signed-in label sits outside Shopify's 44px button. Stretch that button
   * over the icon and the name so one click opens the menu and leaves it open.
   * The menu is anchored to the button, not the viewport edge.
   */
  #bindSignedInAccount() {
    const account = this.querySelector('.account-button:has(.account-button__signed-in) shopify-account');
    if (!account) return;

    const placeDialog = () => {
      const button = account.closest('.account-button');
      const dialog = account.shadowRoot?.querySelector('dialog');
      if (!(button instanceof HTMLElement)) return;

      if (window.innerWidth <= 750) {
        dialog?.style.removeProperty('inset-inline-end');
        return;
      }

      const end = Math.max(0, document.documentElement.clientWidth - button.getBoundingClientRect().right);
      if (dialog) dialog.style.insetInlineEnd = `${Math.round(end)}px`;
    };

    this.#placeAccountDialog = placeDialog;

    const stretch = () => {
      const root = account.shadowRoot;
      if (!root || root.querySelector('[data-homi-account-hit]')) return;
      const style = document.createElement('style');
      style.dataset.homiAccountHit = '';
      style.textContent = `
        #shopify-element-wrapper,
        .wrapper,
        button.account-button {
          width: 100%;
          height: 100%;
          min-width: 0;
          min-height: 100%;
          background: transparent;
          box-shadow: none;
        }
        .account-button__avatar {
          opacity: 0;
        }
      `;
      root.append(style);
    };

    const watch = () => {
      stretch();
      placeDialog();
      if (!account.shadowRoot || this.#signedInAccountObserver) return;
      this.#signedInAccountObserver = new MutationObserver(() => {
        stretch();
        placeDialog();
      });
      this.#signedInAccountObserver.observe(account.shadowRoot, { childList: true });
      account.shadowRoot.addEventListener('toggle', () => requestAnimationFrame(placeDialog), true);
    };

    account.closest('.account-button')?.addEventListener('click', placeDialog, true);
    window.addEventListener('resize', placeDialog);
    this.#accountDialogResize = new ResizeObserver(placeDialog);
    const header = account.closest('.header');
    if (header) this.#accountDialogResize.observe(header);

    if (account.shadowRoot) watch();
    else customElements.whenDefined('shopify-account').then(watch);
  }

  /**
   * Moves the Vitals widget into the static slot and copies the item count.
   * The visible heart stays in Liquid so it does not unmount on navigation.
   */
  #placeWishlistIcon() {
    const column = this.closest('.header__column--right') || this.parentElement;
    const columns = column?.closest('.header__columns');
    const slot = this.querySelector('[data-wishlist-slot]');
    if (!column || !slot) return;

    // On mobile the right column is display:contents, so Vitals can append the
    // widget to the grid or to header-actions instead of the column itself.
    const injected =
      column.querySelector(':scope > .Vtl-WishlistHeaderWrapper') ||
      this.querySelector(':scope > .Vtl-WishlistHeaderWrapper') ||
      columns?.querySelector(':scope > .Vtl-WishlistHeaderWrapper');
    const current = slot.querySelector('.Vtl-WishlistHeaderWrapper');
    const wrap = injected || current;
    if (!wrap) return;

    if (injected && current && injected !== current) current.remove();
    if (wrap.parentElement !== slot) slot.append(wrap);

    this.#observeWishlistCount(wrap);
    this.#syncWishlistCount(wrap);
  }

  /**
   * @param {Element} wrap
   */
  #observeWishlistCount(wrap) {
    if (this.#countObservedWrap === wrap) return;

    this.#wishlistCountObserver?.disconnect();
    this.#countObservedWrap = wrap;
    this.#wishlistCountObserver = new MutationObserver(() => this.#syncWishlistCount(wrap));
    this.#wishlistCountObserver.observe(wrap, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /**
   * @param {Element} wrap
   */
  #syncWishlistCount(wrap) {
    const source = wrap.querySelector('.Vtl-WishlistHeader__HeartCounter');
    if (!source) return;

    this.#setWishlistCount(source.textContent?.trim() ?? '');
  }

  #restoreWishlistCount() {
    try {
      this.#setWishlistCount(sessionStorage.getItem(WISHLIST_COUNT_KEY) ?? '');
    } catch {
      // Private mode can block sessionStorage.
    }
  }

  /**
   * @param {string} value
   */
  #setWishlistCount(value) {
    const countEl = this.querySelector('[data-wishlist-count]');
    if (!countEl) return;

    const normalized = value && value !== '0' ? value : '';
    if (countEl.textContent === normalized && countEl.hidden === !normalized) return;

    countEl.textContent = normalized;
    countEl.hidden = !normalized;

    try {
      sessionStorage.setItem(WISHLIST_COUNT_KEY, normalized);
    } catch {
      // Private mode can block sessionStorage.
    }
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
